import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { apiClient as api } from '../../services/api';
import { Customer, PaymentStatus, Sale, SalesSearchParams } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { useColumnConfiguration } from '../../hooks/useColumnConfiguration';
import { useSales } from '../../hooks/useSales';
import { formatCurrency as formatCurrencyValue } from '../../utils/currency';
import { formatDateTime } from '../../utils/date';
import SaleDetailDialog from './SaleDetailDialog';
import SalesHistoryFilters from './SalesHistoryFilters';
import { SalesHistoryTable } from './SalesHistoryTable';
import { useIsMobileShell } from '../common/useIsMobileShell';
import ListSurface from '../common/ListSurface';
import PaginationFooter from '../common/PaginationFooter';
import { nextTableSort } from '../../utils/tableSort';
import NotebookColumnTab from '../common/NotebookColumnTab';

const defaultSalesDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 3659);
  const format = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  return { startDate: format(start), endDate: format(end) };
};

interface SalesHistoryPanelProps {
  enabled: boolean;
  companyId?: string;
}

/** Owns sales-history query, filters and presentation; SalesPage only coordinates tabs. */
export const SalesHistoryPanel: React.FC<SalesHistoryPanelProps> = ({ enabled, companyId }) => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobileShell();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const defaultRange = useMemo(() => defaultSalesDateRange(), []);
  const numberParam = (key: string, fallback: number) => {
    const rawValue = urlSearchParams.get(key);
    const value = rawValue === null ? fallback : Number(rawValue);
    return Number.isInteger(value) && value >= 0 ? value : fallback;
  };
  const [searchParams, setSearchParams] = useState<SalesSearchParams>({
    page: numberParam('page', 0),
    size: numberParam('size', 10),
    sort: urlSearchParams.get('sort') || 'saleDate',
    direction: urlSearchParams.get('direction') === 'asc' ? 'asc' : 'desc',
    includeItems: true,
    companyId: companyId || urlSearchParams.get('companyId') || undefined,
    startDate: urlSearchParams.get('startDate') || defaultRange.startDate,
    endDate: urlSearchParams.get('endDate') || defaultRange.endDate,
  });
  const [saleNumberFilter, setSaleNumberFilter] = useState(() => urlSearchParams.get('saleNumber') || '');
  const [customerSearchFilter, setCustomerSearchFilter] = useState(() => urlSearchParams.get('customerSearch') || '');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatus | ''>(() => {
    const value = urlSearchParams.get('paymentStatus');
    return value === 'PAID' || value === 'PARTIAL' || value === 'UNPAID' ? value as PaymentStatus : '';
  });
  const [appliedFilters, setAppliedFilters] = useState<Pick<SalesSearchParams, 'saleNumber' | 'customerId' | 'customerSearch' | 'paymentStatus'>>(() => ({
    saleNumber: urlSearchParams.get('saleNumber') || undefined,
    customerId: urlSearchParams.get('customerId') || undefined,
    customerSearch: urlSearchParams.get('customerSearch') || undefined,
    paymentStatus: (urlSearchParams.get('paymentStatus') as PaymentStatus | null) || undefined,
  }));
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [saleDetailDialog, setSaleDetailDialog] = useState(false);

  const { columnVisibility, columnMenuAnchor, handleColumnMenuOpen, ColumnMenu } = useColumnConfiguration({
    defaultColumns: {
      saleNumber: true,
      customer: true,
      saleDate: true,
      totalAmount: true,
      paidAmount: true,
      remainingAmount: false,
      paymentStatus: true,
      createdBy: false,
      notes: false,
      actions: true,
    },
  });

  const salesQueryParams = useMemo<SalesSearchParams>(() => ({
    ...searchParams,
    ...appliedFilters,
  }), [appliedFilters, searchParams]);
  const salesQuery = useSales(salesQueryParams, enabled, t('Failed to load sales'));
  const appliedFilterCount = Number(Boolean(appliedFilters.saleNumber)) + Number(Boolean(appliedFilters.paymentStatus));

  useEffect(() => {
    if (searchParams.companyId === companyId) return;
    setSearchParams(previous => ({ ...previous, companyId, page: 0 }));
  }, [companyId, searchParams.companyId]);

  useEffect(() => {
    const next = new URLSearchParams();
    const urlKeys: Array<keyof SalesSearchParams> = ['companyId', 'page', 'size', 'sort', 'direction', 'startDate', 'endDate'];
    urlKeys.forEach(key => {
      const value = searchParams[key];
      if (value !== undefined && value !== '') next.set(String(key), String(value));
    });
    if (appliedFilters.saleNumber) next.set('saleNumber', appliedFilters.saleNumber);
    if (appliedFilters.customerId) next.set('customerId', appliedFilters.customerId);
    if (appliedFilters.customerSearch) next.set('customerSearch', appliedFilters.customerSearch);
    if (appliedFilters.paymentStatus) next.set('paymentStatus', appliedFilters.paymentStatus);
    if (next.toString() !== urlSearchParams.toString()) {
      setUrlSearchParams(next, { replace: true });
    }
  }, [appliedFilters, searchParams, setUrlSearchParams, urlSearchParams]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setAppliedFilters(current => ({
        ...current,
        customerId: selectedCustomer?.id,
        customerSearch: selectedCustomer ? undefined : customerSearchFilter || undefined,
      }));
      setSearchParams(previous => ({ ...previous, page: 0 }));
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [customerSearchFilter, selectedCustomer]);

  const handleSearch = () => {
    setAppliedFilters({
      saleNumber: saleNumberFilter || undefined,
      customerId: selectedCustomer?.id,
      customerSearch: selectedCustomer ? undefined : customerSearchFilter || undefined,
      paymentStatus: paymentStatusFilter || undefined,
    });
    setSearchParams(previous => ({ ...previous, page: 0 }));
  };

  const handleClearFilters = () => {
    setSaleNumberFilter('');
    setCustomerSearchFilter('');
    setPaymentStatusFilter('');
    setSelectedCustomer(null);
    setCustomerInputValue('');
    setAppliedFilters({});
    setSearchParams({ page: 0, size: 10, sort: 'saleDate', direction: 'desc', companyId, ...defaultSalesDateRange() });
  };

  const handleSortChange = (sort: string) => {
    setSearchParams(previous => ({
      ...previous,
      page: 0,
      ...nextTableSort(previous, sort),
    }));
  };

  const handleRemoveAppliedFilter = (field: 'saleNumber' | 'paymentStatus') => {
    if (field === 'saleNumber') setSaleNumberFilter('');
    if (field === 'paymentStatus') setPaymentStatusFilter('');
    setAppliedFilters(current => ({ ...current, [field]: undefined }));
    setSearchParams(previous => ({ ...previous, page: 0 }));
  };

  const handlePaymentStatusFilter = (status: PaymentStatus) => {
    const nextStatus = appliedFilters.paymentStatus === status ? undefined : status;
    setPaymentStatusFilter(nextStatus ?? '');
    setAppliedFilters(current => ({ ...current, paymentStatus: nextStatus }));
    setSearchParams(previous => ({ ...previous, page: 0 }));
  };

  const loadCustomers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setCustomers([]);
      return;
    }
    try {
      const response = await api.getCustomersPaginated({ page: 0, size: 10, name: searchTerm });
      setCustomers(response.content);
    } catch {
      setCustomers([]);
    }
  };

  const formatCurrency = (amount: number) => formatCurrencyValue(amount);
  const formatDate = (dateString: string) => formatDateTime(dateString, language, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const getPaymentStatusColor = (status: PaymentStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'PAID': return 'success';
      case 'PARTIAL': return 'warning';
      case 'UNPAID': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ pb: isMobile ? 'calc(76px + env(safe-area-inset-bottom))' : 0 }}>
      <ListSurface sx={{ overflow: { xs: 'hidden', md: 'visible' } }}>
      <ListSurface.Toolbar sx={{ px: { xs: 0, md: 0 }, pt: { xs: 0, md: 0 }, pb: { xs: 0, md: 0 } }}>
      <SalesHistoryFilters
        saleNumber={saleNumberFilter}
        paymentStatus={paymentStatusFilter}
        customers={customers}
        selectedCustomer={selectedCustomer}
        customerInputValue={customerInputValue}
        onSaleNumberChange={setSaleNumberFilter}
        onCustomerSelect={(customer) => {
          setSelectedCustomer(customer);
          setCustomerSearchFilter(customer ? `${customer.firstName} ${customer.lastName}` : '');
        }}
        onCustomerInputChange={(value) => {
          if (selectedCustomer && value !== `${selectedCustomer.firstName} ${selectedCustomer.lastName}`) {
            setSelectedCustomer(null);
          }
          setCustomerSearchFilter(value);
          setCustomerInputValue(value);
          void loadCustomers(value);
        }}
        onPaymentStatusChange={setPaymentStatusFilter}
        onSearch={handleSearch}
        onClear={handleClearFilters}
        onRemoveAppliedFilter={handleRemoveAppliedFilter}
        appliedSaleNumber={appliedFilters.saleNumber}
        appliedPaymentStatus={appliedFilters.paymentStatus}
        appliedFilterCount={appliedFilterCount}
      />
      </ListSurface.Toolbar>
      <ListSurface.Content sx={{ position: 'relative' }}>
      <NotebookColumnTab label={t('common.columns')} accent="sales" open={Boolean(columnMenuAnchor)} onClick={handleColumnMenuOpen} />
      <SalesHistoryTable
        sales={salesQuery.data?.content ?? []}
        loading={salesQuery.isLoading}
        error={salesQuery.error}
        searchParams={searchParams}
        columnVisibility={columnVisibility}
        ColumnMenu={ColumnMenu}
        onViewSale={(sale) => {
          setSelectedSale(sale);
          setSaleDetailDialog(true);
        }}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        getPaymentStatusColor={getPaymentStatusColor}
        hasActiveFilters={Object.values(appliedFilters).some(Boolean)}
        onSortChange={handleSortChange}
        onPaymentStatusFilter={handlePaymentStatusFilter}
        activePaymentStatus={appliedFilters.paymentStatus}
      />
      </ListSurface.Content>
      <ListSurface.Footer>
        {(isMobile || (salesQuery.data?.totalElements ?? 0) > 0) && <PaginationFooter
          pageIndex={searchParams.page ?? 0}
          pageSize={searchParams.size ?? 10}
          totalPages={salesQuery.data?.totalPages ?? 0}
          totalElements={salesQuery.data?.totalElements ?? 0}
          showingLabel={t('common.showing')}
          ofLabel={t('common.of')}
          pageSizeLabel={t('common.rowsPerPage')}
          mobileCompact
          previousPageLabel={t('sales.previousPage')}
          nextPageLabel={t('sales.nextPage')}
          onPageChange={pageIndex => setSearchParams(previous => ({ ...previous, page: pageIndex }))}
          onPageSizeChange={size => setSearchParams(previous => ({ ...previous, size, page: 0 }))}
        />}
      </ListSurface.Footer>
      </ListSurface>
      <SaleDetailDialog open={saleDetailDialog} sale={selectedSale} onClose={() => setSaleDetailDialog(false)} />
    </Box>
  );
};

export default SalesHistoryPanel;
